/**
 * RentCafe Cost Estimator — iframe URL (`PropertyId`, `FloorPlanID`); not inline `embed.js`
 * (unreliable floor plan selection). Floor plan ids stay strings (may exceed
 * {@link Number.MAX_SAFE_INTEGER}).
 */
export function rentCafeFloorPlanIdForInit(value) {
    return value.trim();
}
function getSearchParamCaseInsensitive(params, needle) {
    var n = needle.toLowerCase();
    var keys = Array.from(params.keys());
    for (var i = 0; i < keys.length; i += 1) {
        var key = keys[i];
        if (key.toLowerCase() === n) {
            return params.get(key);
        }
    }
    return null;
}
/**
 * `location.search` and `location.hash` (e.g. `/#/path?floorPlans=…`) for OLE links.
 */
function forEachQuerySearchParams(url, fn) {
    if (!url || typeof url !== "string") {
        return;
    }
    var raw = url.trim();
    if (!raw) {
        return;
    }
    try {
        var u = raw.startsWith("http://") || raw.startsWith("https://")
            ? new URL(raw)
            : new URL(raw, "https://placeholder.local/");
        fn(u.searchParams, "search");
        var h = u.hash;
        if (h.length > 1) {
            var withoutHash = h.slice(1);
            var qPart = withoutHash.includes("?")
                ? withoutHash.split("?").slice(1).join("?")
                : withoutHash;
            if (qPart.length > 0 && qPart.includes("=")) {
                try {
                    fn(new URLSearchParams(qPart), "hash");
                }
                catch (_a) {
                    // ignore
                }
            }
        }
    }
    catch (_b) {
        // ignore
    }
}
var FLOORPLAN_ID_PARAM_NAMES = [
    "floorPlans",
    "floorplans",
    "FloorPlanID",
    "floorPlanID",
    "floorplanId",
    "floorplanid",
];
var UNIT_ID_PARAM_NAMES = [
    "UnitID",
    "unitId",
    "unitid",
    "UnitId",
];
/**
 * RentCafe availability / OLE URLs often include the id the cost estimator expects
 * (e.g. floorPlans=…, FloorPlanID=…).
 */
export function extractFloorPlanIdFromAvailabilityURL(url) {
    if (!url || typeof url !== "string") {
        return "";
    }
    if (!url.trim()) {
        return "";
    }
    var found = "";
    forEachQuerySearchParams(url, function (params) {
        if (found.length > 0) {
            return;
        }
        for (var i = 0; i < FLOORPLAN_ID_PARAM_NAMES.length; i += 1) {
            var v = getSearchParamCaseInsensitive(params, FLOORPLAN_ID_PARAM_NAMES[i]);
            if (v === null || v === void 0 ? void 0 : v.trim()) {
                found = v.trim();
                return;
            }
        }
    });
    return found;
}
/**
 * OLE / tour URLs often include `UnitID` for a concrete availability context.
 */
export function extractUnitIdFromAvailabilityURL(url) {
    if (!url || typeof url !== "string") {
        return "";
    }
    if (!url.trim()) {
        return "";
    }
    var found = "";
    forEachQuerySearchParams(url, function (params) {
        if (found.length > 0) {
            return;
        }
        for (var i = 0; i < UNIT_ID_PARAM_NAMES.length; i += 1) {
            var v = getSearchParamCaseInsensitive(params, UNIT_ID_PARAM_NAMES[i]);
            if (v === null || v === void 0 ? void 0 : v.trim()) {
                found = v.trim();
                return;
            }
        }
    });
    return found;
}
/** Matches `MoveInDate=MM/DD/YYYY` in iframe URLs. */
export function formatRentCafeMoveInDate(d) {
    if (d === void 0) { d = new Date(); }
    var mm = String(d.getMonth() + 1).padStart(2, "0");
    var dd = String(d.getDate()).padStart(2, "0");
    var yyyy = d.getFullYear();
    return "".concat(mm, "/").concat(dd, "/").concat(yyyy);
}
/**
 * `url_first` (default): OLE/availability `floorPlans=` (including hash) when present;
 * `meta`: prefer synced `floorplanId` from the Marketing API.
 */
export function resolveRentEstimateFloorPlanId(floorplanId, availabilityURL, options) {
    var _a;
    var fromMeta = rentCafeFloorPlanIdForInit(floorplanId);
    var fromUrl = extractFloorPlanIdFromAvailabilityURL(availabilityURL);
    var prefer = (_a = options === null || options === void 0 ? void 0 : options.preferSource) !== null && _a !== void 0 ? _a : "url_first";
    if (prefer === "meta") {
        if (fromMeta.length > 0 && fromMeta !== "0") {
            return fromMeta;
        }
        return (fromUrl || fromMeta).trim();
    }
    if (fromUrl.length > 0 && fromUrl !== "0") {
        return fromUrl;
    }
    if (fromMeta.length > 0 && fromMeta !== "0") {
        return fromMeta;
    }
    return (fromUrl || fromMeta).trim();
}
export function resolveRentEstimateUnitId(explicit, availabilityURL, options) {
    var useUrlFallback = (options === null || options === void 0 ? void 0 : options.useAvailabilityURLFallback) !== false;
    var fromUrl = useUrlFallback
        ? extractUnitIdFromAvailabilityURL(availabilityURL)
        : "";
    if (explicit !== null && explicit !== undefined) {
        var ex = explicit.toString().trim();
        return (ex || fromUrl).trim();
    }
    return fromUrl.trim();
}
/**
 * Inbound `postMessage` source origin for the RentCafe cost estimator iframe (Yardi guide).
 */
export var RENTCAFE_ESTIMATOR_IFRAME_ORIGIN = "https://www.rentcafe.com";
var RENT_ESTIMATOR_IFRAME_PATH = "/rentestimator/content2/access/rentestimate";
/**
 * Triggers the iframe widget’s “send estimate to email” flow (Yardi: parent → child
 * `postMessage` with `{ action: 'send-estimate', to: email }`). The child may reply
 * with `{ action: 'send-estimate-response', status, message }`.
 */
export function postRentCafeSendEstimateToEmail(iframe, email) {
    var to = email.trim();
    if (to === "") {
        return;
    }
    var win = iframe.contentWindow;
    if (!win) {
        return;
    }
    try {
        win.postMessage({ action: "send-estimate", to: to }, RENTCAFE_ESTIMATOR_IFRAME_ORIGIN);
    }
    catch (_a) {
        // ignore
    }
}
/**
 * `send-estimate-response` sometimes includes `message` as HTML. Strip to visible text
 * for safe display in the parent (no `dangerouslySetInnerHTML` from a third party).
 */
export function plainTextFromRentCafeMessage(value) {
    var _a, _b;
    var t = value.trim();
    if (t === "" || typeof document === "undefined") {
        return t;
    }
    if (!/<[a-z][\s\S]*>/i.test(t)) {
        return t;
    }
    try {
        var doc = new DOMParser().parseFromString(t, "text/html");
        var text = (_b = (_a = doc.body.textContent) === null || _a === void 0 ? void 0 : _a.replace(/\s+/g, " ").trim()) !== null && _b !== void 0 ? _b : "";
        return text !== "" ? text : t;
    }
    catch (_c) {
        return t;
    }
}
/**
 * Decoded token for `URLSearchParams` (e.g. normalize stored `%3d` to `=`); the browser
 * re-encodes in the final query string.
 */
export function normalizedEncryptedPropertyIdForRentCafe(stored) {
    var t = stored.trim();
    if (t === "") {
        return "";
    }
    try {
        return /%[0-9A-Fa-f]{2}/.test(t) ? decodeURIComponent(t) : t;
    }
    catch (_a) {
        return t;
    }
}
/**
 * @deprecated Prefer {@link normalizedEncryptedPropertyIdForRentCafe} + `URLSearchParams`.
 */
export function encodePropertyIdForRentEstimateQuery(stored) {
    var n = normalizedEncryptedPropertyIdForRentCafe(stored);
    if (n === "") {
        return "";
    }
    return encodeURIComponent(n);
}
/**
 * Builds the **official iframe** `src` for the cost estimator: `PropertyId`, `FloorPlanID`,
 * optional `UnitID`, `LeaseTerm`, `MoveInDate`, `noheader` (Yardi / RCO embedding guide).
 */
export function buildRentCafeCostEstimatorIframeUrl(args) {
    var _a, _b, _c;
    var propRaw = normalizedEncryptedPropertyIdForRentCafe(args.encryptedPropertyId);
    if (propRaw === "") {
        return "";
    }
    var fp = resolveRentEstimateFloorPlanId(args.floorplanId, args.availabilityURL, { preferSource: (_a = args.floorPlanIdPreferSource) !== null && _a !== void 0 ? _a : "url_first" }).trim();
    if (fp === "") {
        return "";
    }
    var unit = resolveRentEstimateUnitId((_b = args.unitId) !== null && _b !== void 0 ? _b : null, args.availabilityURL, {
        useAvailabilityURLFallback: args.useAvailabilityURLUnitFallback !== false,
    });
    var params = new URLSearchParams();
    params.set("PropertyId", propRaw);
    params.set("FloorPlanID", fp);
    if (unit.length > 0) {
        params.set("UnitID", unit);
    }
    params.set("LeaseTerm", ((_c = args.leaseTerm) !== null && _c !== void 0 ? _c : "12").toString().trim() || "12");
    var moveIn = args.moveInDate;
    if (moveIn !== null && moveIn !== undefined) {
        var m = String(moveIn).trim();
        if (m.length > 0) {
            params.set("MoveInDate", m);
        }
    }
    var noh = args.noheader;
    if (noh === "0" || noh === "false") {
        // omit noheader
    }
    else {
        params.set("noheader", (noh === null || noh === void 0 ? void 0 : noh.trim()) || "1");
    }
    return "".concat(RENTCAFE_ESTIMATOR_IFRAME_ORIGIN).concat(RENT_ESTIMATOR_IFRAME_PATH, "?").concat(params.toString());
}
/**
 * @deprecated Use {@link buildRentCafeCostEstimatorIframeUrl}. Kept for callers that used the
 * previous helper name. `moveInDate`: `undefined` → today (`MM/DD/YYYY`); `null` / `""` → omit.
 */
export function buildRentEstimateIframeSrc(args) {
    var moveIn = args.moveInDate;
    if (moveIn === undefined) {
        moveIn = formatRentCafeMoveInDate();
    }
    else if (moveIn === "" || moveIn === null) {
        moveIn = null;
    }
    return buildRentCafeCostEstimatorIframeUrl({
        encryptedPropertyId: args.propertyIdEncrypted,
        floorplanId: args.floorplanIdMeta,
        availabilityURL: args.availabilityURL,
        useAvailabilityURLUnitFallback: true,
        moveInDate: moveIn,
        noheader: "1",
    });
}
/**
 * For small numeric RentCafe ids only. Do not use for large floor-plan ids.
 */
export function coerceRentCafeId(value) {
    var trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
        var n = Number.parseInt(trimmed, 10);
        if (!Number.isNaN(n) && Number.isSafeInteger(n)) {
            return n;
        }
    }
    return trimmed || value;
}
/**
 * True when RentCafe settings include a Cost Estimator **encrypted** Property ID
 * (Harmony admin field). If unset, the fees UI should not appear.
 */
export function isRentCafeCostEstimatorPropertyIdConfigured(value) {
    var _a;
    var v = (_a = value === null || value === void 0 ? void 0 : value.toString().trim()) !== null && _a !== void 0 ? _a : "";
    return v.length > 0 && v !== "0";
}
/**
 * @param costEstimatorPropertyId Encrypted site Property ID from Harmony RentCafe settings
 * (not API `propertyId` on the floorplan post).
 */
export function canShowRentCafeFeesEmbed(costEstimatorPropertyId, floorplan) {
    var _a, _b, _c;
    if (!isRentCafeCostEstimatorPropertyIdConfigured(costEstimatorPropertyId)) {
        return false;
    }
    var units = Number(floorplan.unitsAvailable);
    if (Number.isNaN(units) || units < 1) {
        return false;
    }
    var fpid = (_b = (_a = floorplan.floorplanId) === null || _a === void 0 ? void 0 : _a.toString().trim()) !== null && _b !== void 0 ? _b : "";
    var fromUrl = extractFloorPlanIdFromAvailabilityURL((_c = floorplan.availabilityURL) !== null && _c !== void 0 ? _c : "");
    var hasFp = (fpid.length > 0 && fpid !== "0") || fromUrl.length > 0;
    return hasFp;
}
